import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form } from "react-bootstrap";

import { FormSelect } from "../../../../Components/Buttons/FormSelect/FormSelect";
import { LogisticsSavedTypes } from "../../../../Constants/LogisticsSavedEnum";
import { LogisticStatistics } from "./LogisticStatistics/LogisticStatistics";
import { LinesLinkedTypes } from "../../../../Constants/LinesLinkedEnum";
import { ExportPackagesModal } from "./ExportModal/ExportPackagesModal";
import TableTemplate from "../../../../Components/Table/TableTemplate";
import TabManager from "../../../../Components/TabManager/TabManager";
import { LOGISTICS_ENDPOINT } from "../../../../Constants/Endpoints";
import IddinkEmployeeHomeExtra from "./IddinkEmployeeHomeExtra";
import Switch from "../../../../Components/Switch/Switch";

import "./IddinkEmployeeHome.scss";
import { useLogisticsContext } from "../../../../Contexts/Logistics/LogisticsContext";

export function IddinkEmployeeHome() {
  const [t] = useTranslation();
  const tableTemplateRef = useRef(null);
  const iddinkEmployeeHomeExtra = IddinkEmployeeHomeExtra();
  const {
    // Regular Filters
    receiptDate,
    vendorShipmentNo,
    senderName,
    schoolNumber,
    linesLinkedSelected,
    logisticsSavedSelected,
    setReceiptDate,
    setVendorShipmentNo,
    setSenderName,
    setSchoolNumber,
    setLinesLinkedSelected,
    setLogisticsSavedSelected,
    // Page hidden filters
    returnMarkValue,
    barcodeIsLinked,
    disabled,
    setReturnMarkValue,
    setBarcodeIsLinked,
    setBarcodeIsNotLinked,
    setDisabled,
    setExtraReceipts,
    // Paginator
    pageSize,
    currentPage,
    setPageSize,
    setCurrentPage
  } = useLogisticsContext();

  //#region States
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedTabNotLinked, setSelectedTabNotLinked] = useState(0);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportButtonHidden, setExportButtonHidden] = useState(true);

  const [columns, setColumns] = useState(iddinkEmployeeHomeExtra.normalColumns);
  const [showStatistics, setShowStatistics] = useState(false);

  //#endregion

  //#region Effect Hooks
  useEffect(function setInitialTab() {
    setSelectedTab(barcodeIsLinked ? 0 : 1);

    if (!barcodeIsLinked) {
      if (vendorShipmentNo === "EXTRA") {
        setSelectedTabNotLinked(1)
      } else if (disabled === 1) {
        setSelectedTabNotLinked(2)
      } else {
        setSelectedTabNotLinked(0)
      }
    }
  }, [])

  useEffect(() => {
    setColumns(returnMarkValue === 1 ? iddinkEmployeeHomeExtra.returnsColumns : iddinkEmployeeHomeExtra.normalColumns);
    setExportButtonHidden(returnMarkValue === 1);

    tableTemplateRef.current.getItems();
  }, [returnMarkValue]);

  //#endregion

  //#region Handlers
  function handleExportButtonOnClik() {
    setShowExportModal(true);
  }

  function handleExportModalOnExport() {
    setShowExportModal(false);
    tableTemplateRef.current.getItems();
  }

  function handleExportModalOnCancel() {
    setShowExportModal(false);
  }

  function handleClearExtraFiltersParams() {
    setLinesLinkedSelected(" ");
    setLogisticsSavedSelected(" ");
  }

  function handleSwitchOnChange(e) {
    setReturnMarkValue(e ? 1 : 0);
  }

  function handleTableTemplateOnFilter(e) {
    tableTemplateRef.current.getItems();
  }

  function handleNotLinkedTabChange(tabNumber) {
    setCurrentPage(1);
    setSelectedTabNotLinked(0)
    switch (tabNumber) {
      case 0:
        setBarcodeIsLinked();
        break;

      case 1:
        setBarcodeIsNotLinked();
        break;

      case 2:
        setExtraReceipts();
        break;

      case 3:
        setDisabled();
        break;

    }
  }

  function handleTemplateSetPageSize(pageSize) {
    setPageSize(pageSize);
    // tableTemplateRef.current.setPageSize(size);
  }

  function handleSetCurrentPage(newPage) {
    setCurrentPage(newPage);
  }
  //#endregion

  //#region Functions
  function commonExtraFilters() {
    const commonFilters = [
      {
        name: "receiptDate",
        headerCaption: t("ReceiptDate"),
        value: receiptDate,
        component: (
          <Form.Control
            value={receiptDate}
            placeholder="YYYY-MM-DD"
            onChange={(x) => {
              setReceiptDate(x.target.value);
            }}
          />
        ),
      },
      {
        name: "vendorShipmentNo",
        headerCaption: t("VendorShipmentNo"),
        value: vendorShipmentNo,
        component: (
          <Form.Control
            value={vendorShipmentNo}
            onChange={(x) => {
              setVendorShipmentNo(x.target.value);
            }}
          />
        ),
      },
      {
        name: "logisticInbound.senderName",
        headerCaption: t("SenderName"),
        value: senderName,
        component: (
          <Form.Control
            value={senderName}
            onChange={(x) => {
              setSenderName(x.target.value);
            }}
          />
        ),
      },
      {
        name: "school.schoolNumber",
        headerCaption: t("SchoolNumber"),
        value: schoolNumber,
        component: (
          <Form.Control
            value={schoolNumber}
            onChange={(x) => {
              setSchoolNumber(x.target.value);
            }}
          />
        ),
      },
      {
        name: "hasReturnMark",
        headerCaption: t("hasReturnMark"),
        value: returnMarkValue,
        hide: true,
      },
      {
        name: "barcodeIsLinked",
        headerCaption: t("PackageLinked"),
        value: barcodeIsLinked,
        hide: true,
      },
      {
        name: "Disabled",
        headerCaption: t("Disabled"),
        value: disabled,
        hide: true,
      },
      {
        name: "vendorShipmentNo",
        headerCaption: t("VendorShipmentNo"),
        value: vendorShipmentNo,
        hide: true,
      }
    ];

    if (barcodeIsLinked) {
      commonFilters.push(
        {
          name: "allLinesAreLinked",
          headerCaption: t("LinesLinked"),
          value: linesLinkedSelected,
          component: (
            <FormSelect
              placeholder={t("LinesLinked")}
              type="text"
              options={LinesLinkedTypes}
              value={linesLinkedSelected}
              change={(x) => {
                setLinesLinkedSelected(x);
              }}
            />
          ),
        },
        {
          name: "isMappedSaved",
          headerCaption: t("Saved"),
          value: logisticsSavedSelected,
          component: (
            <FormSelect
              placeholder={t("Saved")}
              type="text"
              options={LogisticsSavedTypes}
              value={logisticsSavedSelected}
              change={(x) => {
                setLogisticsSavedSelected(x);
              }}
            />
          ),
        });
    }

    return commonFilters;
  }

  //#endregion

  const logisticsTableComponent = (
    <>
      <div className="button-wrapper">
        <Button
          className="button-view"
          hidden={exportButtonHidden}
          onClick={handleExportButtonOnClik}
        >
          {t("Export")}
        </Button>
        <Button
          className="button-statistics"
          onClick={() => setShowStatistics(true)}
        >
          {t("Statistics")}
        </Button>
        <hr className="switch-separator" />
        <div className="switch-wrapper">
          <p>Returns </p>
          <Switch
            checked={returnMarkValue}
            onChange={handleSwitchOnChange}
          />
        </div>
      </div>
      <TableTemplate
        ref={tableTemplateRef}
        endpoint={LOGISTICS_ENDPOINT}
        columns={columns}
        uniqueId="logisticId"
        paginator={true}
        pageSize={pageSize}
        page={currentPage}
        onSetPageSize={handleTemplateSetPageSize}
        onSetCurrentPage={handleSetCurrentPage}
        onFilter={handleTableTemplateOnFilter}
        extraFilters={commonExtraFilters()}
        clearExtraFiltersParams={handleClearExtraFiltersParams}
      />
    </>
  )

  return (
    <div className="iddink-employee-home-page">
      {!showStatistics && (
        <TabManager
          tabs={[
            {
              title: t("Linked"),
              component: logisticsTableComponent,
              onClick: () => handleNotLinkedTabChange(0),
            },
            {
              title: t("NotLinked"),
              component: (
                <TabManager
                  tabs={[
                    {
                      title: t("Pending"),
                      component: logisticsTableComponent,
                      onClick: () => handleNotLinkedTabChange(1),
                    },
                    {
                      title: t("ExtraReceips"),
                      component: logisticsTableComponent,
                      onClick: () => handleNotLinkedTabChange(2),
                    },
                    {
                      title: t("Disabled"),
                      component: logisticsTableComponent,
                      onClick: () => handleNotLinkedTabChange(3),
                    },
                  ]}
                  changeTab={selectedTabNotLinked}
                />
              ),
              onClick: () => handleNotLinkedTabChange(1),
            },
          ]}
          changeTab={selectedTab}
        />
      )}
      {showStatistics && (
        <LogisticStatistics onBack={() => setShowStatistics(false)} />
      )}
      {showExportModal && (
        <ExportPackagesModal
          show={showExportModal}
          exportOnClick={handleExportModalOnExport}
          cancelOnClick={handleExportModalOnCancel}
          onHide={handleExportModalOnCancel}
        />
      )}
    </div>
  );
}

